/**
 * DeflectionSweepForm — parameter sweep UI for control surface deflections.
 *
 * Reuses the range-parsing pattern from flow360/SweepForm.tsx.
 * Supports range syntax ("−20:5:20") and comma-separated values.
 */

import { useCallback, useMemo } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Select,
  Text,
  TextField,
} from "@radix-ui/themes";
import type { GeometryBody } from "../api/geometry";
import type { SweepMode } from "../stores/geometryStore";

interface Props {
  controlSurfaces: GeometryBody[];
  surfaceAngles: Record<string, string>;
  onSurfaceAnglesChange: (angles: Record<string, string>) => void;
  mode: SweepMode;
  onModeChange: (mode: SweepMode) => void;
  onSubmit: (surfaces: Record<string, number[]>, mode: SweepMode) => void;
  submitting?: boolean;
}

/** Parse range syntax ("−2:0.5:2") or comma-separated values. */
function parseValues(raw: string): number[] {
  const trimmed = raw.trim();
  if (!trimmed) return [];

  const rangeMatch = trimmed.match(
    /^(-?[\d.]+)\s*:\s*(-?[\d.]+)\s*:\s*(-?[\d.]+)$/,
  );
  if (rangeMatch) {
    const [, startStr, stepStr, endStr] = rangeMatch;
    const start = Number(startStr);
    const step = Number(stepStr);
    const end = Number(endStr);
    if (
      step === 0 ||
      (step > 0 && start > end) ||
      (step < 0 && start < end)
    )
      return [start];
    const vals: number[] = [];
    for (
      let v = start;
      step > 0 ? v <= end + 1e-9 : v >= end - 1e-9;
      v += step
    ) {
      vals.push(Math.round(v * 1e6) / 1e6);
      if (vals.length > 200) break;
    }
    return vals;
  }

  return trimmed
    .split(/[,\s]+/)
    .map(Number)
    .filter((n) => !isNaN(n));
}

function ValuePreview({ raw }: { raw: string }) {
  const vals = parseValues(raw);
  if (vals.length <= 1) return null;
  return (
    <Flex gap="1" wrap="wrap" mt="1">
      {vals.map((v, i) => (
        <Badge key={i} size="1" variant="soft" color="blue">
          {v > 0 ? "+" : ""}
          {v}°
        </Badge>
      ))}
    </Flex>
  );
}

export function DeflectionSweepForm({
  controlSurfaces,
  surfaceAngles,
  onSurfaceAnglesChange,
  mode,
  onModeChange,
  onSubmit,
  submitting = false,
}: Props) {
  const caseCount = useMemo(() => {
    const counts = controlSurfaces.map(
      (cs) => parseValues(surfaceAngles[cs.name] ?? "").length || 1,
    );
    if (mode === "full_factorial")
      return counts.reduce((a, b) => a * b, 1);
    if (mode === "one_at_a_time")
      return 1 + counts.reduce((a, b) => a + Math.max(0, b - 1), 0);
    // paired
    return Math.min(...counts);
  }, [controlSurfaces, surfaceAngles, mode]);

  const handleSubmit = useCallback(() => {
    const surfaces: Record<string, number[]> = {};
    for (const cs of controlSurfaces) {
      surfaces[cs.name] = parseValues(surfaceAngles[cs.name] ?? "");
    }
    onSubmit(surfaces, mode);
  }, [controlSurfaces, surfaceAngles, mode, onSubmit]);

  const updateAngle = useCallback(
    (name: string, value: string) => {
      onSurfaceAnglesChange({ ...surfaceAngles, [name]: value });
    },
    [surfaceAngles, onSurfaceAnglesChange],
  );

  if (controlSurfaces.length === 0) {
    return (
      <Text size="2" color="gray">
        No control surfaces defined. Label bodies as control surfaces first.
      </Text>
    );
  }

  return (
    <Box>
      <Flex align="center" gap="3" mb="3">
        <Text size="2" weight="medium">
          Mode:
        </Text>
        <Select.Root
          value={mode}
          onValueChange={(v) => onModeChange(v as SweepMode)}
        >
          <Select.Trigger />
          <Select.Content>
            <Select.Item value="full_factorial">Full Factorial</Select.Item>
            <Select.Item value="one_at_a_time">One at a Time</Select.Item>
            <Select.Item value="paired">Paired</Select.Item>
          </Select.Content>
        </Select.Root>
      </Flex>

      {controlSurfaces.map((cs) => (
        <Card key={cs.name} mb="2" size="1">
          <Flex align="center" gap="2" mb="1">
            <Text size="2" weight="medium">
              {cs.name}
            </Text>
            {cs.cs_type && (
              <Badge size="1" variant="soft" color="iris">
                {cs.cs_type}
              </Badge>
            )}
            {!cs.hinge && (
              <Badge size="1" variant="soft" color="orange">
                no hinge
              </Badge>
            )}
          </Flex>
          <TextField.Root
            size="1"
            value={surfaceAngles[cs.name] ?? ""}
            onChange={(e) => updateAngle(cs.name, e.target.value)}
            placeholder="e.g. -20:5:20 or -20, -10, 0, 10, 20"
          />
          <ValuePreview raw={surfaceAngles[cs.name] ?? ""} />
        </Card>
      ))}

      <Flex align="center" justify="between" mt="3">
        <Text size="2" color="gray">
          {caseCount} case{caseCount !== 1 ? "s" : ""} will be generated
        </Text>
        <Button
          size="2"
          onClick={handleSubmit}
          disabled={submitting || caseCount === 0}
          loading={submitting}
        >
          Create Sweep
        </Button>
      </Flex>
    </Box>
  );
}
